import { useEffect } from 'react';

function upsertTag(selector, create) {
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = create();
    document.head.appendChild(tag);
  }
  return tag;
}

function setMetaName(name, content) {
  upsertTag(`meta[name="${name}"]`, () => {
    const m = document.createElement('meta');
    m.setAttribute('name', name);
    return m;
  }).setAttribute('content', content);
}

function setMetaProp(property, content) {
  upsertTag(`meta[property="${property}"]`, () => {
    const m = document.createElement('meta');
    m.setAttribute('property', property);
    return m;
  }).setAttribute('content', content);
}

function setCanonical(href) {
  upsertTag('link[rel="canonical"]', () => {
    const l = document.createElement('link');
    l.setAttribute('rel', 'canonical');
    return l;
  }).setAttribute('href', href);
}

function absoluteUrl(pathOrUrl) {
  try {
    // If already absolute, URL constructor succeeds without base
    // and origin will be present.
    const u = new URL(pathOrUrl);
    return u.href;
  } catch {
    const { origin } = window.location;
    return origin.replace(/\/$/, '') + (pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`);
  }
}

export function usePageMeta({
  title,
  description,
  image = '/header.PNG',
  type = 'website',
  canonical,
  structuredData,
  noindex = false,
}) {
  useEffect(() => {
    if (title) document.title = title;
    if (description) setMetaName('description', description);

    const url = absoluteUrl(canonical || window.location.pathname);
    setCanonical(url);

    // Open Graph / Twitter
    if (title) setMetaProp('og:title', title);
    if (description) setMetaProp('og:description', description);
    if (type) setMetaProp('og:type', type);
    if (image) setMetaProp('og:image', absoluteUrl(image));
    setMetaProp('og:url', url);

    if (title) setMetaProp('twitter:title', title);
    if (description) setMetaProp('twitter:description', description);
    if (image) setMetaProp('twitter:image', absoluteUrl(image));

    // Robots
    if (noindex) {
      setMetaName('robots', 'noindex, nofollow');
    } else {
      const robots = document.head.querySelector('meta[name="robots"]');
      if (robots) robots.parentElement.removeChild(robots);
    }

    // Page-level JSON-LD (WebPage)
    if (structuredData) {
      const script = upsertTag('script#ld-webpage', () => {
        const s = document.createElement('script');
        s.type = 'application/ld+json';
        s.id = 'ld-webpage';
        return s;
      });
      const base = {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        url,
        name: title,
        description,
      };
      script.textContent = JSON.stringify({ ...base, ...structuredData });
    }
  }, [title, description, image, type, canonical, noindex, structuredData]);
}
